// covered-by: frontends/carrie/src/services/__tests__/multipartUpload.test.ts
// covered-by: frontends/carrie/src/services/__tests__/upload.test.ts
/**
 * One shared budget for the requests carrie makes to S3.
 *
 * A browser opens at most ~6 connections per host over HTTP/1.1, and S3 answers
 * HTTP/1.1. Dropping 20 videos on the grid starts 20 files' part uploads at
 * once, so most of those requests sit in the browser's queue — and each one has
 * already started its stall clock, so it times out before it sends a byte and
 * the whole batch falls back to direct upload. A caller takes a slot from this
 * pool immediately before it hands a request to the browser and gives it back
 * when the request settles, so a held slot always means bytes can move.
 *
 * Cancellation is observed here and nowhere else while a caller is queued: both
 * clocks that bound a request — the stall timer and the total timer — live
 * inside `uploadWithStallDetection`, which cannot start until a slot is granted.
 * So `acquireUploadSlot` takes the caller's `abortSignal`, drops the waiter out
 * of the queue and rejects the moment it fires.
 */

import { UploadCancelledError } from "../utils/stalledUpload";

// why: this is the browser's per-host budget, so it is defined once here and
// the per-file multipart concurrency is derived from it. Two independent 6s
// could drift apart, and then a batch would queue inside the browser again.
export const S3_MAX_CONNECTIONS = 6;

let available = S3_MAX_CONNECTIONS;
const waiting: (() => void)[] = [];

/**
 * Resolves once a slot is free, then holds it until the returned `release` runs.
 * `release` must be called exactly once — `withUploadSlot` does that in a
 * `finally`. Rejects with `UploadCancelledError` if `abortSignal` fires while
 * the caller is queued; a granted slot is never taken back.
 */
export async function acquireUploadSlot(
  abortSignal?: AbortSignal,
): Promise<() => void> {
  if (abortSignal?.aborted) {
    throw new UploadCancelledError();
  }

  if (available > 0) {
    available--;
  } else {
    await new Promise<void>((resolve, reject) => {
      const settle = () => {
        abortSignal?.removeEventListener("abort", onAbort);
        resolve();
      };
      const onAbort = () => {
        const index = waiting.indexOf(settle);
        if (index !== -1) waiting.splice(index, 1);
        reject(new UploadCancelledError());
      };
      waiting.push(settle);
      abortSignal?.addEventListener("abort", onAbort, { once: true });
    });
  }

  return () => {
    const next = waiting.shift();
    if (next) {
      // Hand the slot straight to the next waiter instead of incrementing and
      // re-decrementing, so a queued caller cannot be jumped by a new one.
      next();
    } else {
      available++;
    }
  };
}

/** Runs `run` while holding a slot, releasing it however `run` settles. */
export async function withUploadSlot<T>(
  run: () => Promise<T>,
  abortSignal?: AbortSignal,
): Promise<T> {
  const release = await acquireUploadSlot(abortSignal);
  try {
    return await run();
  } finally {
    release();
  }
}

/** Test helper — restores the pool between suites. */
export function __resetUploadSlotsForTests(): void {
  available = S3_MAX_CONNECTIONS;
  waiting.length = 0;
}
